// Панель выделения: действие над множеством строк живёт здесь, а не в шапке карточки
// (правило 3). Появляется только когда выделено хотя бы одно, и уходит вместе с выделением.
//
// Результат пачки проговаривается СЛОВАМИ, а не числами «3/1». Сервер возвращает updated и
// skipped, где skipped — строки вне области видимости человека; молча проглотить их значит
// оставить его в уверенности, что статус сменился у всех, кого он выделил. По правилу 10 у
// отказа обязана быть причина, и «эти люди не в вашей области видимости» — она и есть.

import { useState } from 'react';

import { RiCloseLine, RiLinkM, RiUserAddLine, RiUserSettingsLine } from '@remixicon/react';
import { Link } from 'react-router';

import { EMPLOYEE_STATUSES, isEmployeeStatus } from '@/shared/api/endpoints/people';
import type { BulkResult, EmployeeStatus } from '@/shared/api/endpoints/people';
import { countLabel } from '@/shared/text/plural';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { PERSON_FORMS, STATUS_TITLES } from '../dictionaries';
import { useBulkStatusMutation } from '../queries';
import type { RegistryTab } from '../route';

const ROW_FORMS = ['строка', 'строки', 'строк'] as const;

export interface BulkStatusBarProps {
  selected: readonly string[];
  /**
   * Вкладка реестра, из которой выделены люди, как её видит человек (при неполной странице
   * вкладок нет, и это «Все»). По ней подтверждение называет, куда ушли строки после смены
   * состояния.
   */
  открытаяВкладка: RegistryTab;
  onClear: () => void;
  /** Пачка применилась: выделение снимается, потому что оно уже отработало. */
  onDone: () => void;
  /**
   * Адрес мастера постановки на объект с этим же выделением.
   *
   * Тз, раздел 5.1: массовые действия реестра — постановка на объект, смена статуса, выгрузка.
   * Первого не было вовсе: отобрал по региону и разряду, отметил шестерых — и дальше открывай
   * шесть карточек по одной.
   */
  onStaff: () => string;
  /**
   * Можно ли править карточки (`core.employee.edit`). Нет — смены статуса и постановки на
   * объект в панели нет вовсе: обе они правка карточки, и показанная кнопка сначала обещала
   * бы, а потом отвечала красным отказом.
   */
  можноПравить: boolean;
  /**
   * Адрес мастера раздачи ссылок для входа с этим же выделением.
   *
   * `undefined` — у человека нет права выдавать ссылки, и кнопки нет вовсе. Это не гейт
   * действия, а ПРЕДЛОЖЕНИЕ: показать его тому, кому нельзя, значит позвать в тупик.
   */
  onInvite?: () => string;
}

export function BulkStatusBar({
  selected,
  открытаяВкладка,
  onClear,
  onDone,
  onStaff,
  onInvite,
  можноПравить,
}: BulkStatusBarProps) {
  const [status, setStatus] = useState(NO_VALUE);
  const [basis, setBasis] = useState('');
  const [gateNote, setGateNote] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const mutation = useBulkStatusMutation(открытаяВкладка);

  function apply() {
    if (!isEmployeeStatus(status)) {
      setGateNote('Сначала выберите статус — иначе непонятно, на какой менять.');
      return;
    }
    if (mutation.isPending) {
      setGateNote('Пачка уже уехала на сервер, ждём ответ. Второй раз не отправляем.');
      return;
    }
    setGateNote(null);
    mutation.mutate(
      {
        employee_ids: [...selected],
        status: status as EmployeeStatus,
        basis: basis.trim() === '' ? null : basis.trim(),
      },
      {
        onSuccess: (value) => {
          setResult(value);
          onDone();
        },
      },
    );
  }

  return (
    <div
      role='group'
      aria-label='Действия над выделенными людьми'
      // Своей подложки нет: панель живёт в подвале колонки, у которого уже есть граница и
      // белый фон. Серый прямоугольник внутри белого подвала — вторая поверхность подряд, и
      // означает она ровно ничего.
      className='flex flex-col gap-2 px-4 py-3'
    >
      <div className='flex items-center justify-between gap-2'>
        <span className='text-label-sm text-text-strong-950'>
          Выделено {countLabel(selected.length, PERSON_FORMS)}
        </span>
        <button
          type='button'
          onClick={onClear}
          className='flex items-center gap-1 text-paragraph-xs text-text-sub-600 underline'
        >
          <RiCloseLine className='size-3.5' />
          Снять выделение
        </button>
      </div>

      {/* Два поля в ряд, а не столбиком: панель стоит в подвале и отнимает высоту у самого
          списка, по которому и выделяют. Столбиком она занимала 260 точек — четверть колонки. */}
      {/* Полей нет вовсе, а не спрятаны стилем: спрятанное остаётся в разметке, его находит
          чтение с экрана и ловит палец на планшете. */}
      {можноПравить && (
        <div className='grid grid-cols-2 gap-2'>
          <SelectField
            id='people-bulk-status'
            label='Новый статус'
            value={status}
            onChange={setStatus}
            options={EMPLOYEE_STATUSES.map((code) => ({ value: code, label: STATUS_TITLES[code] }))}
            placeholder='Выберите статус'
          />

          <TextField
            id='people-bulk-basis'
            label='Основание'
            value={basis}
            onChange={setBasis}
            placeholder='Приказ, заявление'
            hint='Попадёт в историю статусов'
          />
        </div>
      )}

      {/* Два действия над одним выделением. Главная тут смена статуса — она вводится полями
          прямо здесь; постановка на объект уводит в мастер, поэтому обводкой: две одинаково
          заливные кнопки в ряд не отвечают на вопрос, что тут делают обычно. */}
      <div className='flex flex-wrap items-center gap-2'>
        {можноПравить && (
          <Button.Root variant='primary' mode='filled' size='xsmall' type='button' onClick={apply}>
            <Button.Icon as={RiUserSettingsLine} />
            {mutation.isPending ? 'Меняем статус…' : 'Сменить статус пачкой'}
          </Button.Root>
        )}
        {можноПравить && (
          <Button.Root asChild variant='primary' mode='stroke' size='xsmall'>
            <Link to={onStaff()}>
              <Button.Icon as={RiUserAddLine} />
              Поставить на объект
            </Link>
          </Button.Root>
        )}
        {/* Третье действие над тем же выделением: дать людям войти. Ради него всё и просили —
            заказчику надо разом раздать вход своим людям, а не открывать двадцать карточек. */}
        {onInvite !== undefined && (
          <Button.Root
            asChild
            variant='primary'
            mode={можноПравить ? 'stroke' : 'filled'}
            size='xsmall'
          >
            <Link to={onInvite()}>
              <Button.Icon as={RiLinkM} />
              Пригласить выбранных
            </Link>
          </Button.Root>
        )}
      </div>

      {gateNote !== null && <Hint.Root hasError>{gateNote}</Hint.Root>}
      {mutation.isError && <ErrorAlert error={mutation.error} />}
      {result !== null && (
        <Alert.Root
          variant='light'
          status={result.skipped > 0 ? 'warning' : 'success'}
          size='small'
          role='status'
        >
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span>{resultWords(result)}</span>
          </div>
        </Alert.Root>
      )}
    </div>
  );
}

/** Результат пачки словами: что произошло и почему часть строк осталась нетронутой. */
export function resultWords(result: BulkResult): string {
  const updated =
    result.updated === 0
      ? 'Статус не сменился ни у кого'
      : `Статус сменён у ${countLabel(result.updated, PERSON_FORMS)}`;
  if (result.skipped === 0) {
    return `${updated}.`;
  }
  return `${updated}. Пропущено ${countLabel(
    result.skipped,
    ROW_FORMS,
  )}: они вне вашей области видимости, менять их статус может тот, кому эти объекты назначены.`;
}
